import { useEffect, useRef, useState } from 'react'
import { Cart, useCartProps } from '@haus-storefront-react/cart'
import { cartChannel, useEventBusOn } from '@haus-storefront-react/core'
import { Typography, useWidgetReadyAfterInitialLoad, WidgetReadyBoundary } from '@components'
import { useTranslation } from 'react-i18next'
import type { CartSummaryAttributes, CartSummaryConfigStyles } from './_propsFn'
import styles from './CartSummary.module.scss'
import { CartSummaryRow, type CartSummaryItemType } from './CartSummaryRow'

interface CartSummaryContentProps {
  showSubtotal: boolean
  showShipping: boolean
  showTax: boolean
  showTotal: boolean
  labelTypography?: CartSummaryConfigStyles['labelTypography']
  priceTypography?: CartSummaryConfigStyles['priceTypography']
  pricesUpdating: boolean
  isLoading: boolean
  isAdjusting: boolean
  isRemoving: boolean
  order: unknown
  error: unknown
}

interface CartSummaryRowConfig {
  isVisible: boolean
  label: string
  price: number
  priceWithTax: number
  type: CartSummaryItemType
}

const CartSummaryContent = ({
  showSubtotal,
  showShipping,
  showTax,
  showTotal,
  labelTypography,
  priceTypography,
  pricesUpdating,
  isLoading,
  isAdjusting,
  isRemoving,
  order,
  error,
}: CartSummaryContentProps) => {
  const { t } = useTranslation()
  const shouldBlur = pricesUpdating || isAdjusting || isRemoving
  const hasError = Boolean(error)
  const hasOrder = Boolean(order)
  const hasSettledContent = hasOrder || hasError || !isLoading
  const isReady = useWidgetReadyAfterInitialLoad({
    hasSettledContent,
    hasVisibleContent: hasSettledContent,
    isLoading,
  })

  return (
    <WidgetReadyBoundary isReady={isReady}>
      {hasError && (
        <div className={styles.error} role="alert">
          <Typography variant="body">{t('cartSummary.error')}</Typography>
        </div>
      )}
      {hasOrder && (
        <Cart.Summary>
          {({
            totalPrice,
            totalWithTax,
            taxSummary,
            currencyCode,
            shipping,
            shippingWithTax,
            subTotal,
            subTotalWithTax,
          }) => {
            const totalTax = (taxSummary ?? []).reduce((acc, tax) => acc + tax.taxTotal, 0)
            const summaryRows: CartSummaryRowConfig[] = [
              {
                isVisible: showSubtotal,
                label: t('general.subtotal'),
                price: subTotal,
                priceWithTax: subTotalWithTax,
                type: 'subtotal',
              },
              {
                isVisible: showShipping,
                label: t('general.shipping'),
                price: shipping,
                priceWithTax: shippingWithTax,
                type: 'shipping',
              },
              {
                isVisible: showTax,
                label: t('general.tax'),
                price: totalTax,
                priceWithTax: totalTax,
                type: 'tax',
              },
              {
                isVisible: showTotal,
                label: t('general.total'),
                price: totalPrice,
                priceWithTax: totalWithTax,
                type: 'total',
              },
            ]

            return (
              <div className={styles.cartSummary} aria-busy={shouldBlur}>
                {summaryRows
                  .filter((row) => row.isVisible)
                  .map((row) => (
                    <CartSummaryRow
                      key={row.type}
                      price={row.price}
                      priceWithTax={row.priceWithTax}
                      type={row.type}
                      label={row.label}
                      labelTypography={labelTypography}
                      priceTypography={priceTypography}
                      shouldBlur={shouldBlur}
                      currencyCode={currencyCode}
                    />
                  ))}
              </div>
            )
          }}
        </Cart.Summary>
      )}
    </WidgetReadyBoundary>
  )
}

const CartSummaryWidget = ({
  showSubtotal = true,
  showShipping = true,
  showTax = true,
  showTotal = true,
  configStyles,
}: CartSummaryAttributes) => {
  const labelTypography = configStyles?.labelTypography
  const priceTypography = configStyles?.priceTypography
  const [pricesUpdating, setPricesUpdating] = useState(false)
  const { isFetching } = useCartProps()
  const prevFetchingRef = useRef(isFetching)

  useEventBusOn(cartChannel, 'cart:updating', () => setPricesUpdating(true), undefined, false)
  useEventBusOn(cartChannel, 'cart:error', () => setPricesUpdating(false), undefined, false)

  useEffect(() => {
    if (prevFetchingRef.current && !isFetching && pricesUpdating) {
      setPricesUpdating(false)
    }
    prevFetchingRef.current = isFetching
  }, [isFetching, pricesUpdating])

  return (
    <Cart.Root>
      {({ data: order, error, isLoading, isAdjusting, isRemoving }) => (
        <CartSummaryContent
          showSubtotal={showSubtotal}
          showShipping={showShipping}
          showTax={showTax}
          showTotal={showTotal}
          labelTypography={labelTypography}
          priceTypography={priceTypography}
          pricesUpdating={pricesUpdating}
          isLoading={isLoading}
          isAdjusting={isAdjusting}
          isRemoving={isRemoving}
          order={order}
          error={error}
        />
      )}
    </Cart.Root>
  )
}

export default CartSummaryWidget
